import { ExternalLinkIcon, SearchIcon } from '@heroicons/react/solid';
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    updateDoc,
    where,
} from 'firebase/firestore';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { designsCollection } from '../../utils';

const List = (props) => {
    const { title } = props;
    const [loading, setLoading] = useState(false);
    const [designsFetched, setDesignsFetched] = useState(false);
    const [designs, setDesigns] = useState([]);
    const [showNotice, setShowNotice] = useState(false);
    const [designUpdated, setDesignUpdated] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const currentPage = useRef(1);
    const searchTermRef = useRef('');

    const fetchDesigns = useCallback(async () => {
        setLoading(true);
        const pageLimit = currentPage.current ? currentPage.current * 15 : 15;
        let count = 0;
        const fetchedDesigns = [];
        const designsRef = collection(db, designsCollection);
        const term = searchTermRef.current;
        const q =
            term !== ''
                ? query(designsRef, where('product', '==', Number(term)))
                : query(designsRef, orderBy('product', 'desc'), limit(pageLimit));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            count++;
            fetchedDesigns.push({ ...doc.data(), id: doc.id });
        });
        if (fetchedDesigns.length !== 0) {
            setDesigns(fetchedDesigns);
            setDesignsFetched(true);
        }
        setLoading(false);
        console.log('Total: ', count);
    }, []);

    const changeStatus = async (designId, currentStatus) => {
        if (!designId) return;
        if (window.confirm(`¿Desea ${currentStatus === true ? 'desactivar' : 'activar'} este diseño?`)) {
            setShowNotice({ type: 'update', value: false });
            const updateStatus = {
                active: !currentStatus,
            };
            const q = doc(db, designsCollection, designId);
            const designSnap = await getDoc(q);
            if (designSnap.exists()) {
                const designRef = doc(db, designsCollection, designId);
                await updateDoc(designRef, updateStatus);
            }
            setDesignUpdated(true);
            setShowNotice({ type: 'update', value: true });
            setTimeout(() => {
                setShowNotice({ type: 'update', value: false });
            }, 3000);
        }
    };

    const deleteDesign = async (designId, name) => {
        if (!designId) return;
        if (
            window.confirm(
                `¿Está seguro que desea eliminar el diseño ${name}?\n\nRecuerde que puede simplemente desactivarlo para que no aparezca en la tienda.`,
            )
        ) {
            console.log(`Deleting design ${designId} - ${name}`);
            try {
                await deleteDoc(doc(db, designsCollection, designId));

                setDesignUpdated(true);
                setShowNotice({ type: 'delete', value: true });
                setTimeout(() => {
                    setShowNotice({ type: 'delete', value: false });
                }, 3000);
            } catch (error) {
                console.log('Error deleting design: ', error);
            }
        }
    };

    const handleClick = (e) => {
        e.preventDefault();
        currentPage.current = currentPage.current + 1;
        fetchDesigns();
    };

    const handleInputChange = (event) => {
        searchTermRef.current = event.target.value;
        setSearchTerm(event.target.value);
    };

    const handleSearchClick = () => {
        fetchDesigns();
    };

    const clearSearch = () => {
        searchTermRef.current = '';
        setSearchTerm('');
        fetchDesigns();
    };

    useEffect(() => {
        if (designsFetched && !designUpdated) return;
        console.info('Fetch designs!');
        fetchDesigns();
        setDesignUpdated(false);
    }, [designsFetched, designUpdated, fetchDesigns]);

    useEffect(() => {
        document.title = title;
    }, [title]);

    return (
        <>
            {/* Header */}
            <nav className="bg-gray-800 sticky top-0 shadow-lg z-50">
                <div className="mx-auto container px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between">
                        <div className="flex items-center">
                            <div className="shrink-0">
                                <img src="/favicon.png" alt="Stickapp" className="size-8 inline" />{' '}
                                <h1 className="inline text-white font-medium text-xl ml-2">
                                    Stickapp <sup>v3</sup>
                                </h1>
                            </div>
                            <div className="hidden md:block">
                                <div className="ml-10 flex items-baseline space-x-4">
                                    <Link
                                        to={'/admin/list'}
                                        className="rounded-md bg-gray-900 px-3 py-2 uppercase text-xs font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                        Listado de diseños
                                    </Link>
                                    <Link
                                        to={'/admin/create'}
                                        className="rounded-md px-3 py-2 uppercase text-xs font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                        Nuevo diseño
                                    </Link>
                                    {designsCollection === 'designs_dev' && (
                                        <Link
                                            to={'/admin/tools/backfill-created-at'}
                                            className="rounded-md px-3 py-2 uppercase text-xs font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                            Admin
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="hidden md:block">
                            <div className="ml-4 flex items-center md:ml-6">
                                {designsCollection === 'designs_dev' && (
                                    <span className="text-xs uppercase right-1/2 top-0 text-gray-500">
                                        {designsCollection}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <header className="relative bg-gray-100 shadow-sm">
                <div className="mx-auto flex justify-between container px-4 py-4 sm:px-6 lg:px-12">
                    <div className="px-8 py-4">
                        <h1 className="text-2xl font-semibold text-gray-700 ml-12">Listado de diseños</h1>
                    </div>
                    <div className="relative -left-24">
                        <label htmlFor="buscar" className="inline mr-4 uppercase text-xs">
                            Buscar por Id de Producto:
                        </label>
                        <input
                            className="mt-3 p-2 px-4 inline w-50 rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
                            type="text"
                            name="buscar"
                            value={searchTerm}
                            onChange={handleInputChange}
                            placeholder="Ej: 12345"
                        />
                        <button className="cursor-pointer" onClick={handleSearchClick}>
                            <SearchIcon className="relative -left-11 h-8 w-8 text-gray-400 hover:text-etiquette inline" />
                        </button>
                        <button
                            className="cursor-pointer text-sm uppercase relative hover:text-etiquette"
                            onClick={clearSearch}>
                            Limpiar
                        </button>
                    </div>
                </div>
            </header>
            <div
                className={`fixed top-0 p-4 bg-green-300 ${
                    showNotice.value === true ? 'opacity-90' : 'opacity-0'
                } w-full transition-opacity duration-500 ease-in-out shadow-md z-30`}>
                <p className="text-green-800 text-center">
                    {showNotice.type === 'delete' ? 'Diseño eliminado correctamente' : 'Acción realizada correctamente'}
                </p>
            </div>
            <div className="relative container mx-auto my-10 px-10">
                <div className="flex flex-col mb-8">
                    <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                {loading ? (
                                    <div className="text-center py-8">
                                        <div className="lds-ring">
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                            <div></div>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Nombre
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Categoría
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Producto
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Estado
                                                </th>
                                                <th scope="col" className="relative px-6 py-3">
                                                    <span className="sr-only text-etiquette">Editar</span>
                                                    <span className="sr-only text-red-500">Borrar</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {designs?.map((design) => (
                                                <tr key={`${design.id}`}>
                                                    <td className="px-6 py-2 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="shrink-0 h-10 w-10">
                                                                <img
                                                                    className="h-10 w-10 rounded"
                                                                    src={design.thumbnail}
                                                                    alt={design.name}
                                                                />
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {design.name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-2 whitespace-nowrap">
                                                        <div className="ml-4">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {design.category}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-2 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            <a
                                                                href={`https://etiquettediseno.com/wp-admin/post.php?post=${design.product}&action=edit`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="hover:text-etiquette">
                                                                {design.product}
                                                                <ExternalLinkIcon className="h-4 w-4 ml-1 text-etiquette inline" />
                                                            </a>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-2 whitespace-nowrap">
                                                        <button
                                                            className="cursor-pointer"
                                                            onClick={() => changeStatus(design.id, design.active)}>
                                                            <span
                                                                className={`px-2 inline-flex w-full block text-xs leading-5 font-semibold rounded ${
                                                                    design.active
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-gray-100 text-gray-800'
                                                                } hover:bg-green-500 hover:text-white`}>
                                                                {design.active ? 'activo' : 'inactivo'}
                                                            </span>
                                                        </button>
                                                    </td>

                                                    <td className="px-6 py-2 whitespace-nowrap text-right text-sm font-medium">
                                                        <Link
                                                            to={`/admin/${design.id}`}
                                                            className="mx-2 shadow-md rounded-md bg-gray-400 hover:bg-gray-500 text-white px-4 py-2">
                                                            Editar
                                                        </Link>
                                                        <button
                                                            onClick={() => deleteDesign(design.id, design.name)}
                                                            className="cursor-pointer mx-2 shadow-md rounded-md border border-red-200 hover:bg-red-500 text-red-600 hover:text-white px-3 py-1">
                                                            Eliminar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {!searchTerm && (
                    <button
                        onClick={handleClick}
                        className="cursor-pointer shadow rounded-lg bg-etiquette hover:bg-blue-400 text-white hover:text-white px-4 py-2 mt-4 mx-auto">
                        Cargar más
                    </button>
                )}
            </div>
        </>
    );
};

export default List;
